import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import api from '../../services/api';

const EditEquipmentPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [equipmentTypes, setEquipmentTypes] = useState<any[]>([]);
  const [subscription, setSubscription] = useState<any>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [formData, setFormData] = useState({
    equipment_type_id: '',
    brand: '',
    model: '',
    serial_number: '',
    purchase_date: null as Date | null,
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const serviceUserData = localStorage.getItem('serviceUserData');
      if (!serviceUserData) {
        navigate('/app/reminders/dashboard');
        return;
      }
      
      const userData = JSON.parse(serviceUserData);
      const serviceUserId = userData.id;
      const email = userData.email;

      const [typesRes, subRes, equipRes] = await Promise.all([
        api.get('/reminders/equipment-types'),
        api.get(`/reminders/subscription/${serviceUserId}`),
        api.get(`/reminders/equipment?email=${email}`),
      ]);

      setEquipmentTypes(typesRes.data);
      setSubscription(subRes.data);
      
      // Find the specific equipment to edit
      const equipment = equipRes.data.find((e: any) => e.id === id);
      if (equipment) {
        setFormData({
          equipment_type_id: equipment.equipment_type_id || '',
          brand: equipment.brand || '',
          model: equipment.model || '',
          serial_number: equipment.serial_number || '',
          purchase_date: equipment.purchase_date ? new Date(equipment.purchase_date) : null,
          notes: equipment.notes || '',
        });
      } else {
        setError(t('reminders.errors.equipmentNotFound'));
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(t('reminders.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      equipment_type_id: event.target.value,
    });
  };

  const handleDateChange = (value: any) => {
    const date = value instanceof Date ? value : value?.toDate ? value.toDate() : null;
    setFormData({
      ...formData,
      purchase_date: date,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.equipment_type_id || !formData.brand || !formData.model) {
      setError(t('common.fillRequiredFields'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const requestData: any = {
        equipment_type_id: formData.equipment_type_id,
        brand: formData.brand,
        model: formData.model,
        serial_number: formData.serial_number,
        notes: formData.notes,
      };

      if (formData.purchase_date) {
        requestData.purchase_date = formData.purchase_date.toISOString().split('T')[0];
      }

      await api.put(`/reminders/equipment/${id}`, requestData);
      navigate(-1);
    } catch (err: any) {
      console.error('Error updating equipment:', err);
      setError(t('reminders.errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('reminders.editEquipment')}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 10, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <BuildIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Typography variant="h4">
              {t('reminders.editEquipment')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth required>
                <InputLabel>{t('reminders.equipment.type')}</InputLabel>
                <Select
                  value={formData.equipment_type_id}
                  onChange={handleSelectChange}
                  label={t('reminders.equipment.type')}
                >
                  {equipmentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                required
                label={t('reminders.equipment.brand')}
                value={formData.brand}
                onChange={handleInputChange('brand')}
              />

              <TextField
                fullWidth
                required
                label={t('reminders.equipment.model')}
                value={formData.model}
                onChange={handleInputChange('model')}
              />

              <TextField
                fullWidth
                label={t('reminders.equipment.serialNumber')}
                value={formData.serial_number}
                onChange={handleInputChange('serial_number')}
              />

              <DatePicker
                label={t('reminders.equipment.purchaseDate')}
                value={formData.purchase_date}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('reminders.equipment.notes')}
                value={formData.notes}
                onChange={handleInputChange('notes')}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  sx={{ minWidth: 150 }}
                >
                  {saving ? <CircularProgress size={24} /> : t('common.save')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate(-1)}
                  disabled={saving}
                >
                  {t('common.cancel')}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default EditEquipmentPage;